import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, Theme } from '@mui/material';
import { getStyleVariables } from 'styles/vars';
import { getInspectionId } from 'store/inspection/inspectionSelectors';
import { getIsCloningModalOpen } from 'store/app/appSelectors';
import { resetInspection } from 'store/requests';
import { GVTypography } from 'components/lib';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId, parseAllowedOrganizations } from 'utils/auth';
import SessionList from './SessionList';
import InspectionCloningModal from '../../modals/InspectionCloningModal';
import { makeStyles } from 'tss-react/mui';
import { app } from 'store';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      maxWidth: '100%',
      height: '100%',
      overflowX: 'hidden',
      paddingTop: `var(--gds-spacing-4xl)`,
    },
    title: styleVariables.leftPanel.title,
    text: {
      margin: `var(--gds-spacing-xs) var(--gds-spacing-xl)`,
    },
    cloneLink: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'none',
      },
    },
  };
});

const SessionsListLeftPanel = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inspectionId = useSelector(getInspectionId);
  const isCloneModalOpen = useSelector(getIsCloningModalOpen);
  const { user } = useUserStateStore();

  // Check if user belongs to an organization that can clone inspections
  const canCloneInspections = useMemo(() => {
    const userOrgId = getOrganizationId(user);
    if (!userOrgId) return false;

    const allowedOrgsEnv = import.meta.env.VITE_CLONING_ALLOWED_ORGS;
    const allowedOrgs = parseAllowedOrganizations(allowedOrgsEnv);
    const canClone = allowedOrgs.includes(userOrgId);

    return canClone;
  }, [user]);

  useEffect(() => {
    dispatch(resetInspection());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (inspectionId) {
      navigate(`/inspection/${inspectionId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId]);

  const handleOpenCloneModal = () => {
    dispatch(app.actions.setCloningModalOpen(true));
  };

  const handleCloseCloneModal = () => {
    dispatch(app.actions.setCloningModalOpen(false));
  };

  return (
    <Grid container direction="column" className={classes.root} wrap="nowrap">
      <Grid item className={classes.text}>
        <Typography id="home" variant="h2" color="textPrimary">
          Home
        </Typography>
      </Grid>
      <Grid item className={classes.text}>
        <GVTypography id="session" variant="subtitle1" emphasis="medium">
          {canCloneInspections ? (
            <>
              To begin, open an existing session, start a new inspection, or{' '}
              <span className={classes.cloneLink} onClick={handleOpenCloneModal}>
                clone an inspection
              </span>
              .
            </>
          ) : (
            'To begin, open an existing session or start a new inspection.'
          )}
        </GVTypography>
      </Grid>

      <SessionList />

      {/* Clone Inspection Modal - Only render if user can clone */}
      {canCloneInspections && <InspectionCloningModal open={isCloneModalOpen} onClose={handleCloseCloneModal} />}
    </Grid>
  );
};

export default SessionsListLeftPanel;
